import { useCallback, useEffect } from 'react';
import { Controller, UseFieldArrayReturn, useForm } from 'react-hook-form';
import { Button, Input, Modal, Select } from '@shared/widgets';

type TransactionFormProps = {
  addingTransaction: boolean;
  editingTransactionIndex: number | null;
  fieldArray: UseFieldArrayReturn;
  initialData?: TransactionForm | null;
  onSave: (data: TransactionForm) => void;
  setAddingTransaction: (adding: boolean) => void;
};

export interface TransactionForm {
  date?: string;
  amount?: string;
  formCurrency?: string;
  currency?: string;
  merchant?: string;
  formTypeOfDispute?: string;
  typeOfDispute?: string;
  natureOfDispute?: string;
  index?: number;
  id?: string;
}

const currencies = ['AED', 'USD', 'EUR', 'CHF', 'GBP', 'Other'];

const types = ['Unrecognised charge', 'Double transaction', 'Exchange rate', 'Other'];

export const TransactionForm = (props: TransactionFormProps) => {
  const formControl = useForm({
    mode: 'onChange',
    defaultValues:
      props.initialData ||
      ({
        date: '',
        amount: undefined,
        formCurrency: '',
        currency: '',
        merchant: '',
        formTypeOfDispute: '',
        typeOfDispute: '',
        natureOfDispute: '',
        index: props.editingTransactionIndex,
      } as TransactionForm),
  });
  const formCurrency = formControl.watch('formCurrency');
  const formTypeOfDispute = formControl.watch('formTypeOfDispute');
  const handleSaveTransaction = useCallback(
    (data: TransactionForm) => {
      props.onSave(data);
      props.setAddingTransaction(false);
      formControl.reset();
    },
    [props.onSave, props.setAddingTransaction, formControl],
  );

  useEffect(() => {
    if (props.addingTransaction) {
      formControl.reset({
        date: '',
        amount: undefined,
        formCurrency: '',
        currency: '',
        merchant: '',
        formTypeOfDispute: '',
        typeOfDispute: '',
        natureOfDispute: '',
      });
    }
  }, [props.addingTransaction, formControl]);

  useEffect(() => {
    if (props.initialData) {
      formControl.reset(props.initialData);
    }
  }, [props.initialData, formControl]);

  return (
    <Modal title='Add Description' isOpen={props.addingTransaction} onClose={() => props.setAddingTransaction(false)}>
      <form className='text-left' onSubmit={formControl.handleSubmit(handleSaveTransaction)}>
        <Controller
          name='date'
          control={formControl.control}
          rules={{
            maxLength: 100,
          }}
          render={({ field: { value, onChange } }) => (
            <Input
              labelClassName='!text-[#4c4c4c80]'
              inputClassName='!h-[50px] !border-[#4c4c4c1a] !bg-[#fafafa]'
              label='Date'
              name='date'
              required
              value={value}
              onChange={onChange}
              type='date'
            />
          )}
        />
        <Controller
          name='amount'
          control={formControl.control}
          rules={{
            maxLength: 100,
          }}
          render={({ field: { value, onChange } }) => (
            <Input
              labelClassName='!text-[#4c4c4c80]'
              inputClassName='!h-[50px] !border-[#4c4c4c1a] !bg-[#fafafa]'
              label='Amount'
              name='amount'
              required
              value={value}
              onChange={onChange}
              type='number'
            />
          )}
        />
        <Controller
          name='formCurrency'
          control={formControl.control}
          rules={{
            maxLength: 100,
          }}
          render={({ field: { value, onChange } }) => (
            <Select label='Currency' name='formCurrency' value={value} onChange={onChange}>
              <option value='' disabled>
                Select currency
              </option>
              {currencies.map((item, i) => (
                <option value={item} key={i}>
                  {item}
                </option>
              ))}
            </Select>
          )}
        />
        {formCurrency === 'Other' ? (
          <Controller
            name='currency'
            control={formControl.control}
            rules={{
              maxLength: 100,
            }}
            render={({ field: { value, onChange } }) => (
              <Input
                labelClassName='!text-[#4c4c4c80]'
                inputClassName='!h-[50px] !border-[#4c4c4c1a] !bg-[#fafafa]'
                label=' Enter the currency'
                name='currency'
                required
                value={value}
                onChange={onChange}
              />
            )}
          />
        ) : null}
        <Controller
          name='merchant'
          control={formControl.control}
          rules={{
            maxLength: 100,
          }}
          render={({ field: { value, onChange } }) => (
            <Input
              labelClassName='!text-[#4c4c4c80]'
              inputClassName='!h-[50px] !border-[#4c4c4c1a] !bg-[#fafafa]'
              label='Merchant'
              name='merchant'
              required
              value={value}
              onChange={onChange}
            />
          )}
        />
        <Controller
          name='formTypeOfDispute'
          control={formControl.control}
          rules={{
            maxLength: 100,
          }}
          render={({ field: { value, onChange } }) => (
            <Select label='Type of Dispute' name='formTypeOfDispute' value={value} onChange={onChange}>
              <option value='' disabled>
                Select type of dispute
              </option>
              {types.map((item, i) => (
                <option value={item} key={i}>
                  {item}
                </option>
              ))}
            </Select>
          )}
        />
        {formTypeOfDispute === 'Other' ? (
          <Controller
            name='typeOfDispute'
            control={formControl.control}
            rules={{
              maxLength: 100,
            }}
            render={({ field: { value, onChange } }) => (
              <Input
                labelClassName='!text-[#4c4c4c80]'
                inputClassName='!h-[50px] !border-[#4c4c4c1a] !bg-[#fafafa]'
                label='Enter the Type of Dispute'
                name='typeOfDispute'
                required
                value={value}
                onChange={onChange}
              />
            )}
          />
        ) : null}
        <Controller
          name='natureOfDispute'
          control={formControl.control}
          rules={{
            maxLength: 100,
          }}
          render={({ field: { value, onChange } }) => (
            <label className='mb-[22px] block text-[#4c4c4c80]'>
              Nature of Dispute
              <textarea
                className='font-inherit mt-1 block h-[50px] w-full rounded-lg border border-[#4c4c4c1a] bg-[#fafafa] px-4 py-3 text-[#0f0f11]'
                required
                name='natureOfDispute'
                value={value}
                onChange={onChange}
              />
            </label>
          )}
        />
        <Button fullWidth type='submit'>
          Save
        </Button>
      </form>
    </Modal>
  );
};
